"use client";

import { useNow } from "@/hooks/useNow";
import type { SignupStep } from "@/hooks/useSignupFunnel";
import { useSignupResumeStep } from "@/hooks/useSignupResumeStep";
import type { EventSource } from "@/types/discover";
import { computeEventCtaState, isPublicEventJoinable } from "@/utils/eventCta";
import { isPublicEvent, resolveEventHost } from "@/utils/eventHost";
import {
  type CapacityFields,
  pickCapacityFields,
  resolvePublicEventCapacity,
} from "@/utils/publicEventCapacity";
import { useTranslations } from "next-intl";
import Cta from "./Cta";
import { useLiveCapacity, useLiveCapacitySettled } from "./LiveCapacity";
import PublicEventSignupCta from "./PublicEventSignupCta";

type CtaVariant = NonNullable<React.ComponentProps<typeof Cta>["variant"]>;

type EventCtaProps = {
  event: EventSource;
  /** External signup page for club events. Public events open the in-app sheet. */
  signupUrl?: string;
  locale: string;
  /**
   * Whether this instance reopens the signup sheet from `?signup=`. The event
   * page mounts a CTA in both the sidebar and the mobile footer, and both stay
   * in the DOM, so exactly one must claim the URL or each opens its own sheet.
   */
  ownsUrlResume?: boolean;
};

/**
 * The event page's call to action. Ended, archived, deadline-passed, and full
 * states are resolved in the browser because the page's HTML may be cached;
 * capacity comes from the nearest {@link LiveCapacityProvider}.
 */
export default function EventCta({
  event,
  signupUrl,
  locale,
  ownsUrlResume = false,
}: EventCtaProps) {
  const t = useTranslations();
  const now = useNow();
  const host = resolveEventHost(event);
  const urlResumeStep = useSignupResumeStep();
  const capacity = useLiveCapacity(pickCapacityFields(event));
  const capacitySettled = useLiveCapacitySettled();

  if (isPublicEvent(event)) {
    const state = resolvePublicEventCtaState(event, capacity, t, now);
    const resumeStep: SignupStep | undefined = resolveResumeStep(
      ownsUrlResume,
      state.disabled,
      capacitySettled,
      urlResumeStep,
    );
    return (
      <PublicEventSignupCta
        {...state}
        initialStep={resumeStep}
        sheetTitle={t("publicEventSignup.sheetTitle")}
        sheetDescription={t("publicEventSignup.sheetDescription")}
        closeLabel={t("common.close")}
        locale={locale}
        event={event}
        host={host}
      />
    );
  }

  if (!signupUrl) return null;

  const { label, disabled, variant } = computeEventCtaState(event, t, now);
  return (
    <Cta
      href={signupUrl}
      withRegistrationSource
      label={label}
      disabled={disabled}
      variant={variant}
      ctaKind="booking_form"
      clubId={host.clubId}
      eventId={event.id}
      groupId={host.groupId}
      sourceType={host.sourceType}
    />
  );
}

const resolveResumeStep = (
  ownsUrlResume: boolean,
  disabled: boolean,
  capacitySettled: boolean,
  urlResumeStep: SignupStep | undefined,
): SignupStep | undefined => {
  if (!ownsUrlResume || disabled || !capacitySettled) return undefined;
  return urlResumeStep;
};

/**
 * A closed event (ended, archived, deadline passed) wins over a full one: "Event
 * ended" is more definitive than "Fully booked".
 */
function resolvePublicEventCtaState(
  event: EventSource,
  capacity: CapacityFields,
  t: ReturnType<typeof useTranslations>,
  now: Date,
): { label: string; disabled: boolean; variant: CtaVariant } {
  if (!isPublicEventJoinable(event, now)) {
    return {
      label: computeEventCtaState(event, t, now).label,
      disabled: true,
      variant: "secondary",
    };
  }
  if (resolvePublicEventCapacity(capacity).status === "full") {
    return {
      label: t("publicEventSignup.capacityFull"),
      disabled: true,
      variant: "secondary",
    };
  }
  return {
    label: t("publicEventSignup.joinEvent"),
    disabled: false,
    variant: "brand",
  };
}
